import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Mail, Lock, User, Facebook, Github } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useLanguage } from "@/hooks/useLanguage";
import Cookies from "js-cookie";
import { Checkbox } from "@/components/ui/checkbox";
import { logger } from "@/lib/logger"; // Import the logger utility

export default function Auth() {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [isSignUp, setIsSignUp] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [isResetPassword, setIsResetPassword] = useState(false);
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { t, language } = useLanguage();

  useEffect(() => {
    // Debug cookie values using logger instead of console.log
    const cookieValue = Cookies.get("preferredLanguage");
    logger.debug("Auth page loaded. Current language from context:", language);
    logger.debug("Current cookie value (preferredLanguage):", cookieValue);
    
    // Extract referral code from URL if present
    const searchParams = new URLSearchParams(location.search);
    const refCode = searchParams.get('ref');
    
    if (refCode) {
      setReferralCode(refCode);
      logger.debug("Referral code detected:", refCode);
    }
  }, [language, location]);

  useEffect(() => {
    const handleAuth = async () => {
      try {
        const searchParams = new URLSearchParams(window.location.search);
        logger.debug("Raw search string:", window.location.search);
        logger.debug("SearchParams object:", searchParams);
        logger.debug("Type parameter:", searchParams.get('type'));
        logger.debug("Token parameter:", searchParams.get('token'));
        
        // Fallback method in case URLSearchParams isn't working as expected
        const search = window.location.search.substring(1); // Remove the leading '?'
        const manualParams = {};
        search.split('&').forEach(pair => {
          const [key, value] = pair.split('=');
          manualParams[key] = decodeURIComponent(value || '');
        });
        logger.debug("Manually parsed params:", manualParams);
        
        const isRecoveryFlow = searchParams.get('type') === 'recovery';
        const token = searchParams.get('token');

        if (isRecoveryFlow && token) {
          logger.debug("Recovery flow with token detected");
          setIsResetPassword(true);
          return;
        }

        if (!isRecoveryFlow) {
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            navigate("/dashboard");
          }
        }
      } catch (error: any) {
        logger.error("Auth error:", error);
        toast({
          title: t('auth.toast.error.title'),
          description: error.message,
          variant: "destructive",
        });
      }
    };

    handleAuth();
  }, [navigate, toast, t]);

  const validateInputs = () => {
    if (!email || !password) {
      toast({
        title: t('auth.toast.missingInfo.title'),
        description: t('auth.toast.missingInfo.description'),
        variant: "destructive",
      });
      return false;
    }
    
    if (isSignUp && !displayName) {
      toast({
        title: t('auth.toast.missingName.title'),
        description: t('auth.toast.missingName.description'),
        variant: "destructive",
      });
      return false;
    }

    if (isSignUp && (!agreedToPrivacy || !agreedToTerms)) {
      toast({
        title: t('auth.toast.agreementRequired.title'),
        description: t('auth.toast.agreementRequired.description'),
        variant: "destructive",
      });
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast({
        title: t('auth.toast.invalidEmail.title'),
        description: t('auth.toast.invalidEmail.description'),
        variant: "destructive",
      });
      return false;
    }

    if (password.length < 6) {
      toast({
        title: t('auth.toast.invalidPassword.title'),
        description: t('auth.toast.invalidPassword.description'),
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateInputs()) return;

    try {
      setLoading(true);
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        if (error.message === "Email not confirmed") {
          toast({
            title: t('auth.toast.emailNotVerified.title'),
            description: t('auth.toast.emailNotVerified.description'),
            action: (
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    const { error } = await supabase.auth.resend({
                      type: 'signup',
                      email,
                    });
                    if (error) throw error;
                    toast({
                      title: t('auth.toast.verificationEmailSent.title'),
                      description: t('auth.toast.verificationEmailSent.description'),
                    });
                  } catch (resendError: any) {
                    toast({
                      title: t('auth.toast.error.title'),
                      description: resendError.message,
                      variant: "destructive",
                    });
                  }
                }}
              >
                {t('auth.toast.emailNotVerified.resend')}
              </Button>
            ),
            duration: 10000,
          });
        } else if (error.message === "Invalid login credentials") {
          toast({
            title: t('auth.toast.loginFailed.title'),
            description: t('auth.toast.loginFailed.description'),
            variant: "destructive",
          });
        } else {
          toast({
            title: t('auth.toast.error.title'),
            description: error.message,
            variant: "destructive",
          });
        }
        return;
      }

      navigate("/dashboard");
    } catch (error: any) {
      toast({
        title: t('auth.toast.error.title'),
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateInputs()) return;

    try {
      setLoading(true);
      
      const { data: existingProfiles, error: checkError } = await supabase
        .from('profiles')
        .select('id')
        .ilike('display_name', displayName)
        .limit(1);

      if (checkError) throw checkError;

      if (existingProfiles && existingProfiles.length > 0) {
        toast({
          title: t('auth.toast.displayNameTaken.title'),
          description: t('auth.toast.displayNameTaken.description'),
          variant: "destructive",
        });
        return;
      }

      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            display_name: displayName || email.split('@')[0],
          }
        }
      });
      
      if (authError) {
        if (authError.message === "User already registered") {
          toast({
            title: t('auth.toast.userAlreadyRegistered.title'),
            description: t('auth.toast.userAlreadyRegistered.description'),
            variant: "destructive",
          });
        } else {
          toast({
            title: t('auth.toast.error.title'),
            description: authError.message,
            variant: "destructive",
          });
        }
        return;
      }
      
      // If we have a referral code, process it using our Edge Function
      if (referralCode && authData.user) {
        try {
          // Call the Edge Function to process the referral
          const { error: refError } = await supabase.functions.invoke('process-referral', {
            body: { 
              referralCode, 
              userId: authData.user.id 
            }
          });
          
          if (refError) {
            logger.error("Error processing referral:", refError);
          }
        } catch (refProcessError) {
          logger.error("Error calling process-referral function:", refProcessError);
        }
      }
      
      toast({
        title: t('auth.toast.signUpSuccess.title'),
        description: t('auth.toast.signUpSuccess.description'),
      });
    } catch (error: any) {
      toast({
        title: t('auth.toast.error.title'),
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast({
        title: t('auth.toast.missingInfo.title'),
        description: t('auth.toast.missingInfo.description'),
        variant: "destructive",
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast({
        title: t('auth.toast.invalidEmail.title'),
        description: t('auth.toast.invalidEmail.description'),
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth?type=recovery`,
      });

      if (error) throw error;

      toast({
        title: t('auth.toast.resetEmailSent.title'),
        description: t('auth.toast.resetEmailSent.description'),
      });
      setIsForgotPassword(false);
    } catch (error: any) {
      toast({
        title: t('auth.toast.error.title'),
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password || password.length < 6) {
      toast({
        title: t('auth.toast.invalidPassword.title'),
        description: t('auth.toast.invalidPassword.description'),
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);
      const token = new URLSearchParams(window.location.search).get('token');
      
      if (!token) {
        throw new Error("No reset token found");
      }

      // First verify the OTP
      const { error: verifyError } = await supabase.auth.verifyOtp({
        token_hash: token,
        type: 'recovery'
      });

      if (verifyError) {
        throw verifyError;
      }

      // Try to update the password
      const { error: updateError } = await supabase.auth.updateUser({
        password: password
      });

      // Check for errors
      if (updateError) {
        logger.error('Password update error:', updateError);
        
        // If it's a "same password" error, show success anyway but don't sign out
        if (updateError.message && 
            (updateError.message.includes('different from the old password') || 
             updateError.message.includes('same password') ||
             updateError.message.includes('New password should be different'))) {
          
          logger.debug('Same password detected, showing success without signing out');
          
          // Show success message
          toast({
            title: t('auth.toast.resetSuccess.title'),
            description: t('auth.toast.resetSuccess.description'),
          });
          
          // Clear the form and return to login screen without signing out
          window.history.replaceState({}, document.title, "/auth");
          setPassword("");
          setIsResetPassword(false);
          return;
        }
        
        // For any other errors, throw them
        throw updateError;
      }

      // If we reach here, the password was successfully updated
      toast({
        title: t('auth.toast.resetSuccess.title'),
        description: t('auth.toast.resetSuccess.description'),
      });

      // Sign out and redirect
      await supabase.auth.signOut();
      window.history.replaceState({}, document.title, "/auth");
      setPassword("");
      setIsResetPassword(false);
    } catch (error: any) {
      logger.error("Password reset error details:", {
        message: error.message,
        status: error.status,
        name: error.name
      });
      
      toast({
        title: t('auth.toast.error.title'),
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/auth`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          }
        },
      });

      if (error) throw error;
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error: any) {
      logger.error("Google auth error:", error);
      toast({
        title: t('auth.toast.googleLoginError.title'),
        description: t('auth.toast.googleLoginError.description'),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFacebookLogin = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: "facebook",
        options: {
          redirectTo: `${window.location.origin}/auth`,
          queryParams: {
            auth_type: 'rerequest',
            scope: 'email',
          }
        },
      });
      
      if (error) throw error;
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error: any) {
      logger.error("Facebook auth error:", error);
      toast({
        title: t('auth.toast.facebookLoginError.title'),
        description: t('auth.toast.facebookLoginError.description'),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const isRecovery = new URLSearchParams(window.location.search).get('type') === 'recovery';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#1A1F2C] to-[#7E69AB]">
      <div className="absolute inset-0 bg-[url('/background.webp')] bg-cover bg-center opacity-10"></div>
      <div className="max-w-md w-full mx-4 p-8 bg-white/10 backdrop-blur-md rounded-lg shadow-xl relative z-10">
        <div className="flex flex-col items-center mb-8">
          <Logo size="lg" className="text-white mb-4" />
          <h2 className="text-3xl font-bold text-center text-white">
            {isRecovery 
              ? t('auth.title.resetPassword') 
              : (isForgotPassword 
                ? t('auth.title.resetPassword') 
                : t('auth.title.welcome'))}
          </h2>
        </div>
        <form onSubmit={isResetPassword ? handlePasswordReset : (isForgotPassword ? handleForgotPassword : (isSignUp ? handleSignUp : handleLogin))} className="space-y-6">
          {isSignUp && !isRecovery && !isForgotPassword && (
            <div>
              <div className="relative">
                <User className="absolute left-3 top-3 h-5 w-5 text-gray-300" />
                <Input
                  type="text"
                  placeholder={t('auth.form.displayName')}
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="pl-10 bg-white/5 border-gray-500 text-white placeholder:text-gray-400"
                  maxLength={500}
                  required
                />
              </div>
            </div>
          )}
          {!isResetPassword && (
            <div>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-300" />
                <Input
                  type="email"
                  placeholder={t('auth.form.email')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-white/5 border-gray-500 text-white placeholder:text-gray-400"
                  maxLength={500}
                  required
                />
              </div>
            </div>
          )}
          {!isForgotPassword && (
            <div>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-5 w-5 text-gray-300" />
                <Input
                  type="password"
                  placeholder={isResetPassword 
                    ? t('auth.form.newPassword') 
                    : t('auth.form.password')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 bg-white/5 border-gray-500 text-white placeholder:text-gray-400"
                  maxLength={500}
                  required
                  minLength={6}
                />
              </div>
              {!isSignUp && !isResetPassword && (
                <Button
                  type="button"
                  variant="link"
                  className="text-sm text-gray-300 hover:text-white mt-2"
                  onClick={() => setIsForgotPassword(true)}
                >
                  {t('auth.form.forgotPassword')}
                </Button>
              )}
            </div>
          )}
          
          {isSignUp && !isRecovery && !isForgotPassword && (
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="privacy-policy" 
                  checked={agreedToPrivacy}
                  onCheckedChange={(checked) => setAgreedToPrivacy(checked === true)}
                  className="border-gray-500 data-[state=checked]:bg-[#7E69AB] data-[state=checked]:border-[#7E69AB]"
                />
                <label 
                  htmlFor="privacy-policy" 
                  className="text-sm text-gray-300 cursor-pointer"
                >
                  {t('auth.form.privacyPolicyAgreement') + " "} 
                  <a 
                    href="/privacy-policy" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-white hover:underline"
                  >
                    {t('auth.form.privacyPolicy')}
                  </a>
                </label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="terms-of-service" 
                  checked={agreedToTerms}
                  onCheckedChange={(checked) => setAgreedToTerms(checked === true)}
                  className="border-gray-500 data-[state=checked]:bg-[#7E69AB] data-[state=checked]:border-[#7E69AB]"
                />
                <label 
                  htmlFor="terms-of-service" 
                  className="text-sm text-gray-300 cursor-pointer"
                >
                  {t('auth.form.termsAgreement') + " "} 
                  <a 
                    href="/terms-of-service" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-white hover:underline"
                  >
                    {t('auth.form.termsOfService')}
                  </a>
                </label>
              </div>
            </div>
          )}
          
          <div className="space-y-4">
            <Button
              type="submit"
              className="w-full bg-[#7E69AB] hover:bg-[#6E59A5] text-white"
              disabled={loading}
            >
              {loading ? t('auth.form.loading') : (
                isResetPassword 
                  ? t('auth.form.resetPassword') 
                  : (isForgotPassword 
                    ? t('auth.form.sendResetLink') 
                    : (isSignUp 
                      ? t('auth.form.signUp') 
                      : t('auth.form.signIn')
                    )
                  )
              )}
            </Button>
            {!isResetPassword && !isForgotPassword && (
              <Button
                type="button"
                onClick={() => setIsSignUp(!isSignUp)}
                variant="outline"
                className="w-full border-2 border-white bg-white/10 text-white hover:bg-white/20"
                disabled={loading}
              >
                {isSignUp 
                  ? t('auth.form.alreadyHaveAccount') 
                  : t('auth.form.needAccount')}
              </Button>
            )}
            {(isForgotPassword || isResetPassword) && (
              <Button
                type="button"
                onClick={() => {
                  setIsForgotPassword(false);
                  setIsResetPassword(false);
                  if (isResetPassword) {
                    window.history.replaceState({}, document.title, "/auth");
                  }
                }}
                variant="outline"
                className="w-full border-2 border-white bg-white/10 text-white hover:bg-white/20"
                disabled={loading}
              >
                {t('auth.form.backToSignIn')}
              </Button>
            )}
          </div>
        </form>
        {!isResetPassword && !isForgotPassword && (
          <div className="mt-6 space-y-4">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-500"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-transparent text-gray-300">
                  {t('auth.form.orContinueWith')}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Button
                type="button"
                onClick={handleGoogleLogin}
                variant="outline"
                className="border-2 border-white bg-white/10 text-white hover:bg-white/20"
                disabled={loading}
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                  <path d="M1 1h22v22H1z" fill="none" />
                </svg>
                {t('auth.form.google')}
              </Button>
              <Button
                type="button"
                onClick={handleFacebookLogin}
                variant="outline"
                className="border-2 border-white bg-white/10 text-white hover:bg-white/20"
                disabled={loading}
              >
                <Facebook className="w-5 h-5 mr-2" />
                {t('auth.form.facebook')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
